import React, { useEffect, useState } from 'react';
import { useToast } from '../bom/ui/Toast';
import { sbomApi, itemUrl, downloadComponentPdf } from './sbomApi';
import { Support } from './SbomReleases';
import { AssessDrawer, RiskChain, VulnChips } from './SbomAssess';

const utc = (s) => (s ? `${String(s).slice(0, 16)} UTC` : '—');

/**
 * One package on one page (2026-10-21): the Orcanos form of the version opened, every version with
 * the releases that ship it, the vulnerabilities by CVE with their verdicts, the risk → mitigation →
 * test chain of each linked risk, and the check history. Export PDF prints the same record.
 *
 * Our record loads first; the Orcanos form is its own call, so a slow Orcanos never holds the page.
 */
export default function SbomComponentPage({ itemId, info, onAuth, onBack, onOpen }) {
  const { showToast } = useToast();
  const s = info.settings;
  const [d, setD] = useState(null);
  const [form, setForm] = useState(null);
  const [assess, setAssess] = useState(null);
  const [tick, setTick] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let off = false;
    sbomApi.componentDetail(itemId)
      .then((x) => { if (!off) setD(x); })
      .catch((e) => { if (!off && !onAuth(e)) setD({ error: e.message }); });
    return () => { off = true; };
  }, [itemId, tick, onAuth]);

  useEffect(() => {
    let off = false;
    setForm(null);
    sbomApi.componentForm(itemId)
      .then((x) => { if (!off) setForm(x); })
      .catch((e) => { if (!off && !onAuth(e)) setForm({ sections: [], error: e.message }); });
    return () => { off = true; };
  }, [itemId, onAuth]);

  const link = (iid) => itemUrl(info.baseUrl, s.versionId, s.componentType, iid);
  const relLink = (iid) => itemUrl(info.baseUrl, s.versionId, s.releaseType, iid);

  const exportPdf = async () => {
    setBusy(true);
    try {
      const name = await downloadComponentPdf(itemId);
      showToast(`Downloaded ${name}`, 'success');
    } catch (e) {
      if (!onAuth(e)) showToast(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const back = <button type="button" className="sbom-link sbom-cp-back" onClick={onBack}>← Software components</button>;
  if (!d) return <>{back}<div className="sbom-muted">Loading…</div></>;
  if (d.error) return <>{back}<div className="sbom-error" role="alert">{d.error}</div></>;

  const c = d.component;
  const sm = d.summary;
  const cur = d.versions.find((v) => v.item_id === c.item_id) || {};

  return (
    <>
      {back}
      <div className="sbom-head">
        <div style={{ minWidth: 0 }}>
          <div className="sbom-head-t">
            <h2>{d.package.name} <span className="sbom-mono">{c.version}</span></h2>
            {d.package.ecosystem && <span className="sbom-pill sbom-pill--plain">{d.package.ecosystem}</span>}
          </div>
          <div className="sbom-sub">
            {c.key && <a className="sbom-key" href={link(c.item_id)} target="_blank" rel="noreferrer">{c.key} ↗ Open in Orcanos</a>}
            {c.purl && <div className="sbom-mono sbom-tiny sbom-muted" style={{ overflowWrap: 'anywhere' }}>{c.purl}</div>}
          </div>
        </div>
        <span className="sbom-sp" />
        <button type="button" className="sbom-btn" disabled={busy} onClick={exportPdf}>{busy ? 'Preparing…' : 'Export PDF'}</button>
      </div>

      {d.versions.length > 1 && (
        <div className="sbom-cp-vers" role="group" aria-label="Versions">
          <span className="sbom-tiny sbom-muted">Version</span>
          {d.versions.map((v) => (
            <button type="button" key={v.item_id} aria-pressed={v.item_id === c.item_id}
              className={`sbom-pill ${v.item_id === c.item_id ? 'sbom-pill--acc' : 'sbom-pill--plain'} sbom-cp-ver`}
              onClick={() => onOpen(v.item_id)}>
              <span className="sbom-mono">{v.version || '—'}</span>
            </button>
          ))}
        </div>
      )}

      <div className="sbom-stats sbom-cp-stats">
        <div className="sbom-stat"><div className="sbom-stat-n">{sm.versions}</div><div className="sbom-stat-l">Versions</div></div>
        <div className="sbom-stat"><div className="sbom-stat-n">{sm.releases}</div><div className="sbom-stat-l">Releases</div></div>
        {d.vuln_on && (
          <>
            <div className={`sbom-stat ${sm.open ? 'sbom-stat--bad' : sm.vulns ? '' : 'sbom-stat--ok'}`}>
              <div className="sbom-stat-n">{sm.open}</div><div className="sbom-stat-l">Open vulnerabilities</div></div>
            <div className={`sbom-stat ${sm.kev ? 'sbom-stat--bad' : ''}`}>
              <div className="sbom-stat-n">{sm.kev}</div><div className="sbom-stat-l">Actively exploited</div></div>
          </>
        )}
      </div>

      <section className="sbom-cp-sec">
        <h3>Details</h3>
        <div className="sbom-cp-grid">
          <Fact label="Licence" value={cur.licence} mono />
          <Fact label="Supplier" value={cur.supplier} />
          <Fact label="Type" value={cur.type} />
          <Fact label="Support" value={<Support r={cur} />} />
          <Fact label="SOUP class" value={cur.soup_class} />
          {cur.author && <Fact label="Author" value={cur.author} />}
          {cur.url && <Fact label="Web site" value={<a href={cur.url} target="_blank" rel="noreferrer">{cur.url}</a>} />}
          {Object.entries(cur.hashes || {}).slice(0, 2).map(([k, h]) => <Fact key={k} label={k} value={h} mono />)}
        </div>
      </section>

      <section className="sbom-cp-sec">
        <h3>Orcanos fields <span className="sbom-tiny sbom-muted">read live from {c.key || 'the item'}</span></h3>
        {!form && <div className="sbom-muted">Reading the Orcanos form…</div>}
        {form?.error && <div className="sbom-warn">{form.error}</div>}
        {form && !form.error && !form.sections.length && <div className="sbom-muted">The Orcanos item has no fields to show.</div>}
        {form?.sections.map((sec, i) => (
          <div key={i}>
            {sec.name && <div className="sbom-cp-subh">{sec.name}</div>}
            <div className="sbom-cp-grid">
              {sec.fields.map((f, j) => <Fact key={j} label={f.title} value={f.value} />)}
            </div>
          </div>
        ))}
      </section>

      <section className="sbom-cp-sec">
        <h3>Versions and releases</h3>
        <div className="sbom-tw">
          <table className="sbom-table">
            <thead>
              <tr><th>Version</th><th>Item</th><th>Releases</th>{d.vuln_on && <th>Vulnerabilities</th>}{d.vuln_on && <th className="sbom-hide-s">Checked</th>}</tr>
            </thead>
            <tbody>
              {d.versions.map((v) => (
                <tr key={v.item_id} className={v.item_id === c.item_id ? 'sbom-sel' : undefined}>
                  <td className="sbom-mono">
                    {v.item_id === c.item_id ? <b>{v.version || '—'}</b>
                      : <button type="button" className="sbom-link sbom-mono" onClick={() => onOpen(v.item_id)}>{v.version || '—'}</button>}
                  </td>
                  <td>{v.key ? <a className="sbom-key" href={link(v.item_id)} target="_blank" rel="noreferrer">{v.key}</a> : '—'}</td>
                  <td className="sbom-rels">
                    {!v.releases.length && <span className="sbom-muted">No release uses it any more</span>}
                    {v.releases.map((r) => (
                      <a key={r.item_id} className={`sbom-pill ${r.direct ? 'sbom-pill--info' : 'sbom-pill--plain'} sbom-rel-pill`}
                        href={relLink(r.item_id) || undefined} target="_blank" rel="noreferrer"
                        title={r.direct ? 'A direct dependency of this release' : `Brought in by ${r.parents} other package${r.parents === 1 ? '' : 's'}`}>
                        {r.name}{r.direct ? '' : ' · indirect'}
                      </a>
                    ))}
                  </td>
                  {d.vuln_on && <td><VulnChips vulns={v.vulns} checkedAt={v.checked_at} checkable={v.checkable} onOpen={(x) => setAssess(x)} /></td>}
                  {d.vuln_on && <td className="sbom-hide-s sbom-tiny sbom-muted">{v.checked_at ? utc(v.checked_at) : '—'}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {d.vuln_on && (
        <section className="sbom-cp-sec">
          <h3>Vulnerabilities</h3>
          {!d.vulns.length ? (
            <div className="sbom-muted">{d.scan?.ok_at ? 'No open vulnerability is known for any version of this package.' : 'Not checked yet.'}</div>
          ) : (
            <div className="sbom-tw">
              <table className="sbom-table">
                <thead>
                  <tr><th>Vulnerability</th><th>Severity</th><th>Versions</th><th className="sbom-hide-s">Fixed in</th>
                    <th className="sbom-hide-s">Releases exposed</th><th>Assessment</th><th /></tr>
                </thead>
                <tbody>
                  {d.vulns.map((r) => (
                    <tr key={r.vuln_id}>
                      <td>
                        <b className="sbom-mono">{r.vuln_id}</b>
                        {r.kev && <span className="sbom-pill sbom-pill--bad" style={{ marginLeft: 6 }} title="In the CISA Known Exploited Vulnerabilities catalogue">KEV</span>}
                        {r.new && <span className="sbom-pill sbom-pill--acc" style={{ marginLeft: 6 }}>New</span>}
                        {r.summary && <div className="sbom-tiny sbom-muted">{r.summary}</div>}
                      </td>
                      <td><span className={`sbom-chip sbom-chip--${r.severity}`}>{r.severity}{r.score != null ? ` ${r.score}` : ''}</span></td>
                      <td className="sbom-mono">{r.versions.map((x) => x.version).join(', ')}</td>
                      <td className="sbom-hide-s sbom-mono">{r.fixed || '—'}</td>
                      <td className="sbom-hide-s sbom-rels">
                        {r.releases.map((x) => <span key={x.item_id} className="sbom-pill sbom-pill--plain">{x.name}</span>)}
                      </td>
                      <td>
                        <span className={`sbom-pill sbom-pill--${r.verdict.tone}`}>{r.verdict.label}</span>
                        {r.assessment?.risk_key && <div className="sbom-tiny sbom-muted">Risk {r.assessment.risk_key}</div>}
                      </td>
                      <td><button type="button" className="sbom-act" onClick={() => setAssess({ id: r.vuln_id, package_key: r.package_key })}>Assess</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {d.vuln_on && (
        <section className="sbom-cp-sec">
          <h3>Risks</h3>
          {!d.risk_panel && <div className="sbom-muted">No Risk panel is set in Settings → Software BOM, so a vulnerability cannot be linked to a risk.</div>}
          {d.risk_panel && !d.risks.length && (
            <div className="sbom-muted">No vulnerability of this package is assessed <b>Affected</b> with a linked risk. Use <b>Assess</b> above to link one.</div>
          )}
          {d.risk_note && <div className="sbom-warn">{d.risk_note}</div>}
          {d.risks.map((x) => (
            <div key={x.key} className="sbom-cp-risk">
              <div className="sbom-tiny sbom-muted">Linked by {x.vulns.join(', ')}</div>
              {x.missing && <div className="sbom-warn">Risk {x.key} is no longer in the Risk panel's filter.</div>}
              {x.risk && <RiskChain chain={{ risk: x.risk, chains: x.chains }} panelId={d.risk_panel_id} />}
              {!x.risk && !x.missing && <div className="sbom-muted">Risk {x.key}</div>}
            </div>
          ))}
        </section>
      )}

      {d.vuln_on && (
        <section className="sbom-cp-sec">
          <h3>Check history</h3>
          <div className="sbom-tiny sbom-muted" style={{ marginBottom: 6 }}>Last successful check: {utc(d.scan?.ok_at)}</div>
          {!d.gone.length ? (
            <div className="sbom-muted">No vulnerability has stopped being reported for this package.</div>
          ) : (
            <div className="sbom-tw">
              <table className="sbom-table">
                <thead><tr><th>No longer reported</th><th>Version</th><th className="sbom-hide-s">First seen</th><th>Resolved</th></tr></thead>
                <tbody>
                  {d.gone.map((g) => (
                    <tr key={`${g.item_id}-${g.vuln_id}`}>
                      <td><span className="sbom-mono">{g.vuln_id}</span> <span className={`sbom-chip sbom-chip--${g.severity}`}>{g.severity}</span>
                        {g.still_open_elsewhere && <div className="sbom-tiny sbom-muted">Still open on another version</div>}</td>
                      <td className="sbom-mono">{g.version || '—'}</td>
                      <td className="sbom-hide-s sbom-tiny">{utc(g.first_seen)}</td>
                      <td className="sbom-tiny">{utc(g.gone_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {assess && (
        <AssessDrawer vulnId={assess.id} pkg={assess.package_key} info={info} onAuth={onAuth}
          onClose={() => setAssess(null)} onSaved={() => setTick((t) => t + 1)} />
      )}
    </>
  );
}

function Fact({ label, value, mono }) {
  const empty = value === undefined || value === null || value === '';
  return (
    <div className="sbom-cp-fact">
      <div className="sbom-cp-fl">{label}</div>
      <div className={`sbom-cp-fv${mono ? ' sbom-mono' : ''}`}>{empty ? <span className="sbom-muted">—</span> : value}</div>
    </div>
  );
}
